import { AssistantProvider } from '@wianco/portal-assistant';
import '@wianco/portal-assistant/styles.css';
import { useCallback, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { UiAction } from '@wianco/portal-bridge';
import { config } from '@/config/env';
import { useAuthStore } from '@/features/auth/store';
import { useLanguageStore } from '@/features/language/store';
import { useThemeStore } from '@/features/theme/store';
import { defaultNavItems } from '@/features/navigation/config';
import { useAssistantAccess } from './useAssistantAccess';
import { dispatchShopUiAction, getShopCartContext, subscribeShopCartContext } from './shopUiBridge';
import { getAssistantActionSessionToken, resetAssistantActionSession, invalidateAssistantActionContext } from './actionSessionClient';
import { dispatchPortalUiAction,resetPortalUiSession,subscribeServiceActionContext,getServiceActionContext,isActionService } from './portalUiBridge';

const getAuthToken = () => useAuthStore.getState().ensureValidToken(30);

/** The only handoff to Assistant: session lifecycle and explicit route context. */
export function PortalAssistantProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const { canViewAssistant, canUseAssistantAvatars } = useAssistantAccess();
  const userId = useAuthStore((state) => state.user?.sub ?? state.user?.email);
  const customerId = useAuthStore((state) => state.hubspotCustomerId);
  const language = useLanguageStore((state) => state.language);
  const theme = useThemeStore((state) => state.theme);
  const grants = useAuthStore((state) => JSON.stringify([state.user?.permissions, state.user?.roles]));
  const actionService=location.pathname.split('/')[1]??'';
  useEffect(() => { resetPortalUiSession(); resetAssistantActionSession(); }, [authenticated, userId, customerId, grants]);
  useEffect(()=>{resetAssistantActionSession();},[actionService]);
  useEffect(()=>subscribeServiceActionContext(invalidateAssistantActionContext),[]);
  useEffect(() => subscribeShopCartContext(invalidateAssistantActionContext), []);
  const getAssistantSessionToken = useCallback(() => getAssistantActionSessionToken(actionService), [actionService]);
  const onActionsSettled = useCallback(async () => {
    if(getShopCartContext()?.ready) await dispatchShopUiAction({ service: 'shop', action: 'shop.cart.refresh', payload: {} }, navigate);
    const refresh={
      academy:{service:'academy',action:'academy.content.refresh',payload:{}},
      library:{service:'library',action:'library.tree.refresh',payload:{}},
      portfolio:{service:'portfolio',action:'portfolio.data.refresh',payload:{}},
      support:{service:'support',action:'support.data.refresh',payload:{}}
    } as const;
    if(isActionService(actionService)&&actionService!=='shop'&&getServiceActionContext(actionService)?.ready)
      if (Object.prototype.hasOwnProperty.call(refresh,actionService)) await dispatchPortalUiAction(refresh[actionService as keyof typeof refresh],navigate);
  }, [navigate,actionService]);
  const onUiAction = useCallback((action: UiAction) => dispatchPortalUiAction(action, navigate), [navigate]);
  const area = defaultNavItems.find(
    (item) =>
      item.path &&
      (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
  );
  const settings = config().assistant;
  return (
    <AssistantProvider
      sessionKey={authenticated ? (userId ?? 'authenticated') : null}
      enabled={canViewAssistant}
      allowAvatars={canUseAssistantAvatars}
      apiBaseUrl={settings?.apiBaseUrl ?? 'https://app.elunos.ai'}
      publicToken={settings?.publicToken ?? 'wgt_dee8b51208903454119e1c8ec08cb50b'}
      getAuthToken={getAuthToken}
      getAssistantSessionToken={settings?.actionApiBaseUrl ? getAssistantSessionToken : undefined}
      onActionsSettled={onActionsSettled}
      onUiAction={onUiAction}
      context={{
        pathname: location.pathname,
        service: area?.id,
        title: area?.label,
        language,
        customerId,
      }}
      theme={theme}
    >
      {children}
    </AssistantProvider>
  );
}
